import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import parse, { domToReact } from "html-react-parser";
import { ApplicationState } from "../../../store";
import { IPackage, IProduct } from "../../../store/protectionPlanService/business/types";
import { ISmbLicenseLimit } from "../../../store/protectionPlanService/business/types";
import { useTranslation } from "react-i18next";

interface IProps {
    closeDialog: any;
    showUpgradeDialog: any;
    packageIndex: number;
    licenseLimit: string;
}

const BusinessProtectionPlanUpgradeConfirmation: React.FunctionComponent<IProps> = (props) => {
    const packageList = useSelector((state: ApplicationState) => {
        return state.protectionPlanBusinessState?.protectionPlanData?.smbLicenseLimits?.find((it: ISmbLicenseLimit) => it.key === props.licenseLimit)?.packageList;
    });
    const currentPackage = packageList?.find((pkg) => pkg.IsActive);
    const selectedPackage = (packageList && packageList.length >= props.packageIndex ? packageList[props.packageIndex] : null) as IPackage;

    const showPackageUpgradeDialog = () => {
        props.showUpgradeDialog();
    };
    const { t: tLang } = useTranslation();

    useEffect(() => {
        window.addEventListener("keyup", (evt: any) => {
            evt = evt || window.event;
            var isEscape = false;
            if ("key" in evt) {
                isEscape = evt.key === "Escape" || evt.key === "Esc";
            } else {
                isEscape = evt.keyCode === 27;
            }
            if (isEscape) {
                props.closeDialog();
            }
        });
        return () => {
            window.removeEventListener("keyup", () => {});
        };
    });

    const displayProduct = (product: IProduct, index: number) => {
        if (!product.isChildProduct && selectedPackage?.ActionButtonText?.toLowerCase() === "purchase") {
            return (
                <li key={"product_" + index}>
                    <em className="las la-check-circle inline-icon status-good"></em>
                    {product.displayName && <span> {parse(product.displayName)}</span>}
                </li>
            );
        } else if (!product.isChildProduct && selectedPackage?.ActionButtonText?.toLowerCase() === "cancel") {
            return (
                <li key={"product_" + index}>
                    <em className="las la-minus-circle inline-icon status-bad"></em>
                    {product.displayName && <span> {parse(product.displayName)}</span>}
                </li>
            );
        }
    };

    const displayConfirmationMessage = () => {
        if (selectedPackage?.ActionButtonText?.toLowerCase() === "purchase") {
            return (
                <p>
                    {tLang("manageAccount.protectionPlan.changePlanConfirmationPopup.purchasePlanMsg.text1")} {selectedPackage?.PackageName}
                    {tLang("manageAccount.protectionPlan.changePlanConfirmationPopup.purchasePlanMsg.business")}
                </p>
            );
        } else if (selectedPackage?.ActionButtonText?.toLowerCase() === "upgrade") {
            return (
                <p>
                    {tLang("manageAccount.protectionPlan.changePlanConfirmationPopup.upgradePlanMsg.text1")} {currentPackage?.PackageName}{" "}
                    {tLang("manageAccount.protectionPlan.changePlanConfirmationPopup.upgradePlanMsg.text2")} {selectedPackage?.PackageName}{" "}
                    {tLang("manageAccount.protectionPlan.changePlanConfirmationPopup.upgradePlanMsg.business")}
                </p>
            );
        } else if (selectedPackage?.ActionButtonText?.toLowerCase() === "downgrade") {
            return (
                <p>
                    {tLang("manageAccount.protectionPlan.changePlanConfirmationPopup.downgradePlanMsg.text1")} {currentPackage?.PackageName}{" "}
                    {tLang("manageAccount.protectionPlan.changePlanConfirmationPopup.downgradePlanMsg.text2")} {selectedPackage?.PackageName}{" "}
                    {tLang("manageAccount.protectionPlan.changePlanConfirmationPopup.downgradePlanMsg.business")}
                </p>
            );
        } else {
            return <p>{tLang("manageAccount.protectionPlan.changePlanConfirmationPopup.cancelBusinessPlanMsg")}</p>;
        }
    };

    const dialogTitle = () => {
        if (selectedPackage?.ActionButtonText?.toLowerCase() === "purchase") {
            return tLang("manageAccount.protectionPlan.changePlanConfirmationPopup.confirmPurchase");
        } else if (selectedPackage?.ActionButtonText?.toLowerCase() === "upgrade") {
            return tLang("manageAccount.protectionPlan.changePlanConfirmationPopup.confirmUpgrade");
        } else if (selectedPackage?.ActionButtonText?.toLowerCase() === "downgrade") {
            return tLang("manageAccount.protectionPlan.changePlanConfirmationPopup.confirmDowngrade");
        } else {
            return tLang("manageAccount.protectionPlan.changePlanConfirmationPopup.confirmCancelation");
        }
    };

    const confirmButtonTitle = () => {
        if (selectedPackage?.ActionButtonText?.toLowerCase() === "purchase") {
            return tLang("manageAccount.protectionPlan.changePlanConfirmationPopup.continuePurchase");
        } else if (selectedPackage?.ActionButtonText?.toLowerCase() === "upgrade") {
            return tLang("manageAccount.protectionPlan.changePlanConfirmationPopup.continueUpgrade");
        } else if (selectedPackage?.ActionButtonText?.toLowerCase() === "downgrade") {
            return tLang("manageAccount.protectionPlan.changePlanConfirmationPopup.continueDowngrade");
        } else {
            return tLang("manageAccount.protectionPlan.changePlanConfirmationPopup.continueCancelation");
        }
    };

    return (
        <React.Fragment>
            <div className="dialog-header">
                <div className="dialog-icon">
                    <em className="las la-coins"></em>
                </div>
                <h1>{dialogTitle()}</h1>
            </div>

            <div className="dialog-body">
                {displayConfirmationMessage()}

                <ul className="list-reset">
                    {selectedPackage?.ActionButtonText?.toLowerCase() === "purchase"
                        ? selectedPackage?.Products?.map((product: IProduct, index: number) => displayProduct(product, index))
                        : selectedPackage?.ProductDifference?.map((product: IProduct, index: number) => displayProduct(product, index))}
                </ul>
            </div>

            <div className="dialog-footer">
                <div className="row between-xs">
                    <div className="col-xs col-no-gutter flex-grow-null">
                        <button
                            type="button"
                            className="btn-default btn-link btn-no-padding btn-no-border"
                            id="btn-cancel-upgrade-confirmation"
                            onClick={() => {
                                window.addToDataLayer({ event: "Protection Plan - Close confirmation" });
                                props.closeDialog();
                            }}
                        >
                            <span className="btn-text">{tLang("manageAccount.protectionPlan.btn.cancel")}</span>
                        </button>
                    </div>
                    <div className="col-xs col-no-gutter flex-grow-null">
                        <button
                            type="button"
                            className="btn-default btn-primary"
                            id="btn-submit-upgrade-confirmation"
                            onClick={() => {
                                window.addToDataLayer({ event: "Protection Plan - Continue " + selectedPackage.ActionButtonText?.toUpperCase() });
                                showPackageUpgradeDialog();
                            }}
                        >
                            <span className="btn-text">{confirmButtonTitle()}</span>
                        </button>
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
};
export default BusinessProtectionPlanUpgradeConfirmation;
